/**
 * Iter 92 — mobile-first customer dashboard helpers.
 * NextRentalHero: prominent "Your next rental" card (spec §10).
 * CustomerBottomNav: fixed one-tap nav on <768px (spec §9).
 * TripStatusChips: Upcoming / Active / Past / Cancelled filters (spec §1).
 */
import { Link, useNavigate } from "react-router-dom";
import { Home, CalendarDays, MessageCircle, UserCircle2, MapPin, ChevronRight, Phone, CheckCircle2 } from "lucide-react";
import Money from "@/components/Money";

const DAY = 24 * 60 * 60 * 1000;

export function tripBucket(b) {
  const s = (b.status || "").toLowerCase();
  if (["cancelled", "canceled", "declined_by_host", "expired_no_response", "no_show"].includes(s)) return "cancelled";
  if (s === "completed") return "past";
  const now = new Date();
  const start = new Date(b.start_date);
  const end = new Date(b.end_date);
  if (start <= now && now <= end) return "active";
  if (end < now) return "past";
  return "upcoming";
}

export function TripStatusChips({ counts, active, onChange }) {
  const chips = [
    ["all", "All"], ["upcoming", "Upcoming"], ["active", "Current"],
    ["past", "Past"], ["cancelled", "Cancelled"],
  ];
  return (
    <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 mb-4" data-testid="trip-status-chips">
      {chips.map(([key, label]) => (
        <button
          key={key}
          type="button"
          onClick={() => onChange(key)}
          className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold border transition ${
            active === key
              ? "bg-neutral-900 text-white border-neutral-900"
              : "bg-white text-neutral-600 border-border hover:border-neutral-400"
          }`}
          data-testid={`trip-chip-${key}`}
        >
          {label}
          <span className={`ml-1.5 text-xs tabular-nums ${active === key ? "text-white/70" : "text-neutral-400"}`}>
            {counts[key] || 0}
          </span>
        </button>
      ))}
    </div>
  );
}

export function NextRentalHero({ bookings = [] }) {
  const now = new Date();
  const next = bookings
    .filter((b) => tripBucket(b) === "upcoming" && ["confirmed", "pending_approval", "pending_payment"].includes((b.status || "").toLowerCase()))
    .sort((a, b) => String(a.start_date).localeCompare(String(b.start_date)))[0];
  if (!next) return null;
  const start = new Date(next.start_date);
  const daysLeft = Math.max(0, Math.ceil((start - now) / DAY));
  const time = String(next.start_date).length > 10 ? String(next.start_date).slice(11, 16) : null;
  return (
    <div className="mb-5 rounded-3xl bg-neutral-900 text-white p-5 sm:p-6 relative overflow-hidden" data-testid="next-rental-hero">
      <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-amber-400/15 pointer-events-none" />
      <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-amber-300 mb-2">Your next rental</div>
      <div className="flex items-center gap-4 relative z-10">
        <div className="flex-1 min-w-0">
          <div className="font-display text-xl sm:text-2xl font-medium truncate" data-testid="next-rental-car">
            {next.car?.make} {next.car?.model}
          </div>
          <div className="text-sm text-white/70 mt-1 flex items-center gap-1.5 flex-wrap">
            {next.pickup_location && (<span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {next.pickup_location}</span>)}
            <span>· {String(next.start_date).slice(0, 10)}{time ? ` • ${time}` : ""}</span>
          </div>
          <div className="inline-flex items-center mt-2 px-2.5 py-1 rounded-full bg-amber-400 text-neutral-900 text-xs font-bold" data-testid="next-rental-countdown">
            {daysLeft === 0 ? "Pickup today" : `Pickup in ${daysLeft} day${daysLeft > 1 ? "s" : ""}`}
          </div>
        </div>
        <Link
          to={`/agreements/${next.id}`}
          className="shrink-0 inline-flex items-center gap-1 rounded-full bg-white text-neutral-900 hover:bg-amber-100 px-4 py-2.5 text-sm font-semibold transition"
          data-testid="next-rental-view"
        >
          View <ChevronRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}

/** Iter 94 — reminder banner shown ≤24h before pickup: directions + host contact. */
export function TripReminderBanner({ bookings = [] }) {
  const now = new Date();
  const soon = bookings
    .filter((b) => {
      if (!["confirmed", "host_accepted"].includes((b.status || "").toLowerCase())) return false;
      const start = new Date(b.start_date);
      const diff = start - now;
      return diff > -2 * 60 * 60 * 1000 && diff <= DAY;
    })
    .sort((a, b) => String(a.start_date).localeCompare(String(b.start_date)))[0];
  if (!soon) return null;
  const start = new Date(soon.start_date);
  const hoursLeft = Math.max(0, Math.round((start - now) / (60 * 60 * 1000)));
  const time = String(soon.start_date).length > 10 ? String(soon.start_date).slice(11, 16) : null;
  const vendor = soon.car?.vendor || {};
  const wa = String(vendor.whatsapp || "").replace(/[^\d+]/g, "");
  const mapsHref = soon.pickup_location
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(soon.pickup_location)}&dir_action=navigate`
    : null;
  return (
    <div className="mb-5 rounded-3xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 p-5" data-testid="trip-reminder-banner">
      <div className="flex items-start gap-3">
        <span className="h-10 w-10 rounded-2xl bg-amber-400 text-neutral-900 flex items-center justify-center shrink-0">
          <CalendarDays className="h-5 w-5" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-neutral-900" data-testid="trip-reminder-title">
            {hoursLeft <= 1 ? "Pickup is now!" : `Pickup in ${hoursLeft} hours`} — {soon.car?.make} {soon.car?.model}
          </div>
          <div className="text-sm text-neutral-600 mt-0.5">
            {soon.pickup_location && (<span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {soon.pickup_location}</span>)}
            {time && <span> · {time}</span>}
          </div>
        </div>
      </div>
      {/* Iter 95 — pre-pickup checklist. */}
      <ul className="mt-3 rounded-2xl bg-white/70 border border-amber-200 p-3 space-y-1.5" data-testid="trip-reminder-checklist">
        {[
          "Driving license (valid, physical card)",
          "Credit/debit card for the deposit",
          `Booking code #${(soon.id || "").slice(0, 8).toUpperCase()}`,
        ].map((item) => (
          <li key={item} className="flex items-center gap-2 text-sm text-neutral-700">
            <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
            {item}
          </li>
        ))}
      </ul>
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 mt-3">
        {mapsHref && (          <a
            href={mapsHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-neutral-900 text-white text-sm font-semibold py-2.5 sm:px-4 hover:bg-neutral-800 transition"
            data-testid="trip-reminder-directions"
          >
            <MapPin className="h-4 w-4" /> Directions
          </a>
        )}
        {wa && (
          <>
            <a
              href={`tel:${wa}`}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-white border border-amber-300 text-sm font-semibold py-2.5 sm:px-4 hover:border-amber-400 transition"
              data-testid="trip-reminder-call"
            >
              <Phone className="h-4 w-4" /> Call host
            </a>
            <a
              href={`https://wa.me/${wa.replace("+", "")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-semibold py-2.5 sm:px-4 hover:bg-emerald-100 transition"
              data-testid="trip-reminder-whatsapp"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
          </>
        )}
      </div>
    </div>
  );
}

export function CustomerBottomNav({ activeTab, setActiveTab }) {  const navigate = useNavigate();
  const items = [
    { key: "home", label: "Home", icon: Home, onTap: () => navigate("/") },
    { key: "trips", label: "Reservations", icon: CalendarDays, onTap: () => setActiveTab("trips") },
    { key: "messages", label: "Messages", icon: MessageCircle, onTap: () => navigate("/messages") },
    { key: "profile", label: "Profile", icon: UserCircle2, onTap: () => setActiveTab("profile") },
  ];
  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-border flex items-stretch pb-[env(safe-area-inset-bottom)]"
      data-testid="customer-bottom-nav"
    >
      {items.map((it) => {
        const Icon = it.icon;
        const active = (it.key === "trips" && activeTab === "trips") || (it.key === "profile" && activeTab === "profile");
        return (
          <button
            key={it.key}
            type="button"
            onClick={it.onTap}
            className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 text-[11px] font-semibold transition ${active ? "text-blue-600" : "text-neutral-500"}`}
            data-testid={`bottom-nav-${it.key}`}
          >
            <Icon className="h-5 w-5" />
            {it.label}
          </button>
        );
      })}
    </nav>
  );
}

/** Iter 93 — transmission / fuel chips for reservation cards. */
export function VehicleChips({ car }) {
  if (!car) return null;
  const chips = [];
  if (car.transmission) chips.push(car.transmission);
  if (car.fuel_type) chips.push(car.fuel_type);
  if (car.year) chips.push(String(car.year));
  if (!chips.length) return null;
  return (
    <span className="inline-flex flex-wrap gap-1 ml-1 align-middle">
      {chips.map((c) => (
        <span key={c} className="px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-600 text-[10px] font-semibold uppercase tracking-wide">
          {c}
        </span>
      ))}
    </span>
  );
}

/** Iter 93 — paid online vs remaining at pickup breakdown for reservation cards. */
export function PaymentBreakdown({ booking }) {
  const paid = Number(booking.amount_paid_online ?? 0);
  const due = Number(booking.amount_due_at_pickup ?? 0);
  if (!paid && !due) return null;
  const refunded = booking.payment_status === "refunded";
  return (
    <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`payment-breakdown-${booking.id}`}>
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold ${refunded ? "bg-neutral-100 text-neutral-500 line-through" : "bg-emerald-50 text-emerald-700"}`}>
        Paid online: <Money amount={paid} />
      </span>
      {due > 0 && !refunded && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 text-[11px] font-semibold">
          Due at pickup: <Money amount={due} />
        </span>
      )}
      {refunded && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-sky-50 text-sky-700 text-[11px] font-semibold">
          Refunded
        </span>
      )}
    </div>
  );
}

/** Iter 93 — large touch-friendly action buttons + quick contacts. */
export function CustomerCardActions({ booking }) {
  const vendor = booking.car?.vendor || {};
  const wa = String(vendor.whatsapp || "").replace(/[^\d+]/g, "");
  const cancelled = ["cancelled", "canceled", "declined_by_host", "expired_no_response"].includes(booking.status);
  const openMessages = () => {
    const opener = document.querySelector(`[data-testid="open-messages-${booking.id}"]`);
    if (opener) opener.click();
    const panel = document.querySelector(`[data-testid="messages-panel-${booking.id}"]`);
    if (panel) setTimeout(() => panel.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
  };
  return (
    <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 mt-3 pt-3 border-t border-border" data-testid={`card-actions-${booking.id}`}>
      <Link
        to={`/agreements/${booking.id}`}
        className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-neutral-900 text-white text-sm font-semibold py-3 sm:py-2 sm:px-4 hover:bg-neutral-800 transition"
        data-testid={`card-view-${booking.id}`}
      >
        View reservation
      </Link>
      {!cancelled && (
        <button
          type="button"
          onClick={openMessages}
          className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-blue-50 border border-blue-200 text-blue-700 text-sm font-semibold py-3 sm:py-2 sm:px-4 hover:bg-blue-100 transition"
          data-testid={`card-message-${booking.id}`}
        >
          <MessageCircle className="h-4 w-4" /> Message
        </button>
      )}
      {!cancelled && wa && (
        <>
          <a
            href={`tel:${wa}`}
            className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-white border border-border text-sm font-semibold py-3 sm:py-2 sm:px-4 hover:border-neutral-400 transition"
            data-testid={`card-call-${booking.id}`}
          >
            <Phone className="h-4 w-4" /> Call host
          </a>
          <a
            href={`https://wa.me/${wa.replace("+", "")}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-semibold py-3 sm:py-2 sm:px-4 hover:bg-emerald-100 transition"
            data-testid={`card-whatsapp-${booking.id}`}
          >
            WhatsApp
          </a>
        </>
      )}
    </div>
  );
}

const _TIMELINE_STEPS = ["Booked", "Payment received", "Confirmed", "Vehicle pickup", "Vehicle returned"];

/** Iter 93 — Booked → Confirmed → Pickup → Returned progress (details page). */
export function BookingTimeline({ booking }) {
  if (!booking) return null;
  const cancelled = ["cancelled", "canceled", "declined_by_host", "expired_no_response"].includes(booking.status);
  const now = new Date();
  const done = [
    true,
    booking.payment_status === "paid" || booking.payment_status === "refunded",
    ["confirmed", "completed", "host_accepted"].includes(booking.status),
    booking.status === "completed" || (booking.status === "confirmed" && new Date(booking.start_date) <= now),
    booking.status === "completed",
  ];
  return (
    <div className="rounded-2xl border border-border bg-white p-4 mb-4" data-testid="booking-timeline">
      {cancelled ? (
        <div className="text-sm font-semibold text-rose-600">This reservation was {booking.status.replace(/_/g, " ")}.</div>
      ) : (
        <ol className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0">
          {_TIMELINE_STEPS.map((label, i) => (
            <li key={label} className="flex sm:flex-1 items-center gap-2">
              <span
                className={`h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${
                  done[i] ? "bg-emerald-500 text-white" : "bg-neutral-200 text-neutral-500"
                }`}
                data-testid={`timeline-step-${i}`}
              >
                {done[i] ? "✓" : "○"}
              </span>
              <span className={`text-xs font-medium ${done[i] ? "text-neutral-900" : "text-neutral-400"}`}>{label}</span>
              {i < _TIMELINE_STEPS.length - 1 && (
                <span className={`hidden sm:block flex-1 h-0.5 mx-2 rounded ${done[i + 1] ? "bg-emerald-400" : "bg-neutral-200"}`} />
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
